import React, {useEffect, useRef, useState} from "react";
import styles from "./UserInvitationModal.module.scss";
import CustomerInformation from "./CustomerInformation";

import {Modal, ModalSize, Spinner} from "@renta-apps/athenaeum-react-components";
import {Box, Button, ButtonSize, ButtonType, Checkbox, ContentBox, Dropdown, Icon, IconSize, IDropdownItem, LoadingDecorator, Text} from "@renta-apps/renta-react-components";
import Localizer from "@/localization/Localizer";
import InviteUserPostRequest, {constructRolePermission, InvitedUser} from "@/models/server/InviteUserPostRequest";
import InviteUserRequest from "@/models/server/Requests/InviteUserRequest";
import InviteUserPageDataResponse from "@/models/server/Responses/InviteUserPageDataResponse";
import RegisterResponse from "@/models/server/Responses/RegisterResponse";
import RentaEasyController from "@/pages/RentaEasyController";
import {inviteUserAsync} from "@/services/UserService";

interface CheckboxItem {
    id: string;
    label: string;
    description?: string;
    disabled: boolean;
    visible: boolean;
    onChange?: (checkedState: Record<string, boolean>, setCheckedState: React.Dispatch<React.SetStateAction<Record<string, boolean>>>, setDisabledState: React.Dispatch<React.SetStateAction<Record<string, boolean>>>) => void;
}

interface UserInvitationModalProps {
    contractId: string;
    organizationId?: string;
    constructionSiteId: string | null;
    isOpen: boolean;
    onClose(): void;
}

const UserInvitationModal: React.FC<UserInvitationModalProps> = ({contractId, constructionSiteId, isOpen, onClose, organizationId}) => {
    const _modalRef = useRef<Modal>(null);

    const [loading, setLoading] = useState(true);
    const [inviteUserPageData, setInviteUserPageData] = useState<InviteUserPageDataResponse | null>(null);
    const [constructionSiteName, setConstructionSiteName] = useState<string>("");
    const [invitedUsers, setInvitedUsers] = useState<InvitedUser[]>([]);
    const [checkedState, setCheckedState] = useState<Record<string, boolean>>(
        () => ({
            inviteToAll: false,
            mainRole: false,
            noRentApproval: false,
            canSeePrices: false,
        })
    );
    const [disabledState, setDisabledState] = useState<Record<string, boolean>>(
        () => ({
            inviteToAll: false,
            mainRole: false,
            noRentApproval: false,
            canSeePrices: false,
        })
    );
    const [constructionSiteOptions, setConstructionSiteOptions] = useState<IDropdownItem[]>([]);
    const [selectedConstructionSites, setSelectedConstructionSites] = useState<IDropdownItem[]>([]);
    const [isSelectConstructionSitesVisible, setIsSelectConstructionSitesVisible] = useState(false);

    const [isBeingSaved, setIsBeingSaved] = useState(false);
    const [hasSaveFailed, setHasSaveFailed] = useState(false);

    const checkboxData: CheckboxItem[] = [
        {
            id: "inviteToAll",
            label: Localizer.userInvitationModalPermissionsOrganizationUser,
            description: Localizer.userInvitationModalPermissionsOrganizationUserDescription,
            disabled: false,
            visible: constructionSiteId === null,
        },
        {
            id: "mainRole",
            label: Localizer.userInvitationModalPermissionsMainRole,
            description: constructionSiteId === null
                ? Localizer.userInvitationModalPermissionsOrganizationMainRoleDescription
                : Localizer.userInvitationModalPermissionsConstructionSiteMainRoleDescription,
            disabled: false,
            visible: true,
            onChange: (checkedState, setCheckedState, setDisabledState) => {
                const isChecked = !checkedState["mainRole"];
                setCheckedState((prev) => ({
                    ...prev,
                    mainRole: isChecked,
                    noRentApproval: isChecked,
                    canSeePrices: isChecked,
                }));
                setDisabledState((prev) => ({
                    ...prev,
                    noRentApproval: isChecked,
                    canSeePrices: isChecked,
                }));
            },
        },
        {
            id: "noRentApproval",
            label: Localizer.userInvitationModalPermissionsNoRentApproval,
            description: Localizer.userInvitationModalPermissionsNoRentApprovalDescription,
            disabled: false,
            visible: true,
        },
        {
            id: "canSeePrices",
            label: Localizer.userInvitationModalPermissionsCanSeePrices,
            disabled: false,
            visible: true,
        },
    ];

    useEffect(() => {
        if (isOpen) {
            _modalRef.current?.openAsync();
        } else {
            clearValues();
            _modalRef.current?.closeAsync();
        }
    }, [_modalRef, isOpen]);

    useEffect(() => {
        if (!isOpen) {
            return;
        }

        const fetchData = async () => {
            try {
                const request: InviteUserRequest = {
                    organizationId: organizationId ?? null,
                    contractId,
                    constructionSiteId,
                };

                const data: InviteUserPageDataResponse = await RentaEasyController.getInvitationPageData(request);
                setInviteUserPageData(data);
            } catch (error) {
                console.error("Error fetching invite user page data", error);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [isOpen, contractId, constructionSiteId]);

    useEffect(() => {
        if (!inviteUserPageData) return;

        const constructionSite = constructionSiteId
            ? inviteUserPageData.constructionSiteAccesses.find((x) => x.id === constructionSiteId)
            : null;

        setConstructionSiteName(constructionSite?.name || "");

        //If the modal is opened for a top-level company, all permission checkboxes should be preselected and disabled.
        if (inviteUserPageData.masterContract) {
            setCheckedState({
                inviteToAll: true,
                mainRole: true,
                noRentApproval: true,
                canSeePrices: true,
            });
            setDisabledState({
                inviteToAll: true,
                mainRole: true,
                noRentApproval: true,
                canSeePrices: true,
            });

            return;
        }

        setConstructionSiteOptions(
            inviteUserPageData.constructionSiteAccesses.map((item) => ({
                name: item.name,
                value: item.id,
                group: item.isOpen ? Localizer.constructionSitesActive : Localizer.constructionSitesNonActive,
                description: null,
            }))
        );
    }, [inviteUserPageData, constructionSiteId]);

    useEffect(() => {
        // Hide dropdown if "Invite to all construction sites of the company" is selected.
        // Or if the modal is opened from a construction site page.
        setIsSelectConstructionSitesVisible(!checkedState["inviteToAll"] && constructionSiteId === null);
    }, [checkedState, constructionSiteId]);

    const addInvitedUser = (user: InvitedUser) => {
        setInvitedUsers((prev) => [...prev, user]);
    };

    const removeInvitedUser = (index: number) => {
        const newInvitedUsers = [...invitedUsers];
        newInvitedUsers.splice(index, 1);
        setInvitedUsers(newInvitedUsers);
    };

    const clearValues = ()  => {
        setInvitedUsers([]);
        setCheckedState(() => ({
            inviteToAll: false,
            mainRole: false,
            noRentApproval: false,
            canSeePrices: false,
        }));
    }

    const handleCheckboxChange = (id: string) => {
        const checkbox = checkboxData.find((item) => item.id === id);
        if (checkbox?.onChange) {
            checkbox.onChange(checkedState, setCheckedState, setDisabledState);
        } else {
            setCheckedState((prev) => ({
                ...prev,
                [id]: !prev[id],
            }));
        }
    };

    const saveChangesAsync = async () => {
        setIsBeingSaved(true);
        setHasSaveFailed(false);

        try {
            const constructionSiteAccesses = inviteUserPageData!.constructionSiteAccesses
            .filter(item =>
                constructionSiteId !== null ?
                    item.id === constructionSiteId :
                    selectedConstructionSites.some(selected => selected.value === item.id)
            )
            .map(item => ({
                ...item,
                access: true,
                fullAccess: checkedState["mainRole"],
                directRentAccess: checkedState["noRentApproval"],
                pricesVisible: checkedState["canSeePrices"],
            }));

            const request: InviteUserPostRequest = {
                organizationId: organizationId ?? null,
                contractId,
                usersList: invitedUsers,
                rolePermission: constructRolePermission(checkedState["mainRole"], checkedState["noRentApproval"], checkedState["canSeePrices"]),
                accessToAllConstructionSites: checkedState["inviteToAll"],
                companyMainUserRole: checkedState["mainRole"],
                constructionSiteAccesses,
            };

            const result: RegisterResponse = await inviteUserAsync(request);

            if (result.errorMessage !== "") {
                setHasSaveFailed(true);
            }
            else {
                setHasSaveFailed(false);
                onClose();
            }
        }
        catch (error) {
            console.error("Error inviting user", error);
            setHasSaveFailed(true);
        }
        finally {
            setIsBeingSaved(false);
        }
    };

    const shouldDisableAddUserButton = () => {
        if (isSelectConstructionSitesVisible && selectedConstructionSites.length === 0) {
            return true;
        }

        if (invitedUsers.length === 0) {
            return true;
        }

        return false;
    };

    return isOpen ? (
        <Modal id="user-invitation-modal"
               ref={_modalRef}
               preventEsc preventClosingOnOutsideClick preventClosingOnInsideClick
               size={ModalSize.ExtraLarge}
               title={Localizer.userInvitationModalTitle}
               className={styles.modal}
               onClose={async () => onClose()}
        >
            <LoadingDecorator className={styles.loadingContainer} hideContentOnLoading={true} isLoading={loading}>
                <Box display="flex" flexDirection="column" gap={20}>
                    <ContentBox title={Localizer.userInvitationModalHeader}
                                subTitle={Localizer.userInvitationModalSubHeader}
                                className={styles.mainBox}
                    >
                        <Box display="flex" flexDirection="column" alignItems="flex-start">
                            <Text tag={"label"} size={"Large"} weight="bold">
                                {Localizer.userInvitationModalActiveCompany}
                            </Text>
                            <Text tag={"label"} data-cy={"customerName"} transform={"rentaCapitalize"}>
                                {`${inviteUserPageData?.organizationContract?.name ?? inviteUserPageData?.organization?.name} - ${inviteUserPageData?.organizationContract?.vatId ?? inviteUserPageData?.organization?.vatId}`}
                            </Text>
                        </Box>

                        {constructionSiteId && (
                            <Box display="flex" flexDirection="column" alignItems="flex-start">
                                <Text tag={"label"} size={"Large"} weight="bold">
                                    {Localizer.userInvitationModalActiveConstructionSite}
                                </Text>
                                <Text tag={"label"} data-cy={"constructionSiteName"} transform={"rentaCapitalize"}>
                                    {constructionSiteName}
                                </Text>
                            </Box>
                        )}
                    </ContentBox>

                    <ContentBox title={Localizer.userInvitationModalUserHeader}
                                subTitle={Localizer.userInvitationModalUserSubHeader}
                                className={styles.customerInformation}
                    >
                        <CustomerInformation invitedUsers={invitedUsers} onAddUser={addInvitedUser}/>
                    </ContentBox>

                    <ContentBox title={Localizer.userInvitationModalListTitle}
                                subTitle={Localizer.userInvitationModalListHeader}
                                className={styles.inviteList}
                    >
                        {invitedUsers.length > 0 && <Box display="flex" flexDirection="column" p={3} gap={12} className={styles.dataTable}>
                            <Box display="flex" flexDirection="column" p={1}>
                                {invitedUsers.map((user, index) => (
                                    <React.Fragment key={index}>
                                        <Box display="flex" flexDirection="column" pt={12} pr={72} pb={16} pl={16}
                                            className={index % 2 === 0 ? styles.oddItem : ''}>
                                            <Text tag="span" size="Normal" className={styles.ellipsisText}>
                                                {`${user.firstName} ${user.lastName}`}
                                            </Text>
                                            <Text tag="span" size="Normal" weight="normal" className={styles.ellipsisText} data-cy="invited-user-data-table-email">
                                                {user.email}
                                            </Text>
                                            <Text tag="span" size="Normal" weight="normal" className={styles.ellipsisText}>
                                                {user.telephone}
                                            </Text>
                                        </Box>

                                        <div className={styles.actions}>
                                            <Button size={ButtonSize.Medium}
                                                    type={ButtonType.Icon}
                                                    className={styles.deleteButton}
                                                    onClick={() => removeInvitedUser(index)}
                                                    data-cy="delete-invited-user-button"
                                            >
                                                <Icon name="xMark" size={IconSize.Medium} />
                                            </Button>
                                        </div>
                                    </React.Fragment>
                                ))}
                            </Box>
                        </Box>
                        }
                    </ContentBox>

                    <ContentBox title={Localizer.userInvitationModalPermissionsTitle}
                                subTitle={Localizer.userInvitationModalPermissionsHeader}
                                className={styles.permissions}
                    >
                        {checkboxData.map(({ id, label, description, visible }) => (
                            visible ? (
                                <React.Fragment key={id}>
                                    <div className={styles.actions}>
                                        <Checkbox id={id}
                                                  className={styles.checkbox}
                                                  checked={checkedState[id]}
                                                  disabled={disabledState[id]}
                                                  onChange={() => handleCheckboxChange(id)}
                                        />
                                    </div>
                                    <Box display="flex" flexDirection="column" pb={16} pl={32}>
                                        <Text tag="label" size="Normal">{label}</Text>
                                        {description && (
                                            <Text tag="span" size="Small" className={styles.information} data-cy="checkbox-description">
                                                {description}
                                            </Text>
                                        )}
                                    </Box>
                                    {id === "inviteToAll" && isSelectConstructionSitesVisible && (
                                        <Box display="flex" flexDirection="column" alignItems="flex-start" className={styles.selectConstructionSites}>
                                            <Dropdown id={`select-construction-sites`}
                                                      className={styles.selectConstructionSitesDropdown}
                                                      items={constructionSiteOptions}
                                                      selectedItem={selectedConstructionSites}
                                                      label={Localizer.userInvitationModalPermissionsSelectConstructionSites}
                                                      onChange={(items: IDropdownItem[]) => setSelectedConstructionSites(items)}
                                                      allowEmpty
                                                      multiSelect
                                                      searchable
                                            />
                                            <Text tag="span" size="Small" weight="medium" className={styles.information} data-cy="select-construction-sites-description">
                                                {Localizer.userInvitationModalPermissionsSelectConstructionSitesDescription}
                                            </Text>
                                        </Box>
                                    )}
                                </React.Fragment>
                            ) : null
                        ))}
                    </ContentBox>

                    <Box display="flex" flexDirection="column" pt={8} gap={16}>
                        {hasSaveFailed && <div className={styles.activeButtons}>
                                <Text tag="span" size="Normal" className={styles.errorText} data-cy="save-error">
                                    {Localizer.userInvitationModalSendingInvetesFailed}
                                </Text>
                            </div>
                        }

                        {!isBeingSaved && <div className={styles.activeButtons}>
                            <Button type={ButtonType.Secondary}
                                    id={`cancel-invite-user-button`}
                                    onClick={() => onClose()}
                            >
                                {Localizer.componentModalCancel}</Button>
                            <Button type={ButtonType.Primary}
                                    id={`save-invite-user-button`}
                                    disabled={shouldDisableAddUserButton()}
                                    onClick={async () => await saveChangesAsync()}
                            >
                                {Localizer.userInvitationModalSendInvites}
                            </Button>
                        </div>
                        }

                        {isBeingSaved && <div id="user-invitation-modal-action-loader" className={styles.activeButtons}>
                            <div className={styles.spinnerContainer}>
                                <Spinner noShading/>
                            </div>
                            <div className={styles.textContainer}>
                                <span>{Localizer.fleetMonitoringPageSubscribeToAlertsModalPleaseWait}</span>
                            </div>
                        </div>
                        }
                    </Box>
                </Box>
            </LoadingDecorator>
        </Modal>
    ) : null;
}

export default UserInvitationModal;